// @greenlight-spec: 10-security-compliance.md
// @greenlight-spec: 28-it-dashboard-ui.md
import * as React from 'react';

import type {
  BillingEntitlementEnforcement,
  FleetReportingEnforcement,
  UpstreamStatus,
} from '@greenlight/shared';

export type Role = 'org_admin' | 'admin' | 'user';

export interface CurrentUser {
  id: string;
  email: string;
  role: Role;
  groups: string[];
  /**
   * Present only while this dashboard session is an impersonation (ADR-0033).
   * `id`/`email`/`role` above are the *effective* (impersonated) identity; this
   * names the real operator so the shell can show the banner and gate Stop.
   */
  impersonating?: { operator_user_id: string; operator_email: string | null };
  /**
   * Drives the shell's fleet-reporting banner. Carried on identity because the
   * banner renders for every role while the underlying delivery diagnostics are
   * org-admin only. Absent renders no banner.
   */
  fleet_reporting?: FleetReportingEnforcement;

  /**
   * Drives the shell's entitlement banner, for the same reason: every role needs
   * to know why building stopped. Absent renders no banner.
   */
  billing_entitlement?: BillingEntitlementEnforcement;
  /**
   * Drives the shell's upstream-status banner. Carried on identity for the same
   * reason as `fleet_reporting`: the banner renders for every role while the
   * full upstream block is org-admin only. Critical vendors only.
   */
  upstream_status?: UpstreamStatus;
  /**
   * Whether the org has accepted Greenlight's data-access terms. False means the
   * next integration registration must carry acceptance.
   */
  data_access_terms_accepted?: boolean;
  /**
   * The org's open "GitHub Actions is blocked" episode: GitHub refused to start a
   * workflow run for billing, so no app's pipeline can run. Absent renders no banner.
   */
  actions_blocked?: ActionsBlocked;
}

export interface ActionsBlocked {
  since: string;
  /** The run whose GitHub annotation showed the block, when there was one. */
  run_url: string | null;
  /** The GitHub organization login, which names its billing page. */
  scm_org_handle: string;
}

/** POST `/api/auth/impersonation/start` — begin impersonating a user. */
export async function startImpersonation(userId: string): Promise<Response> {
  return fetch('/api/auth/impersonation/start', {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ user_id: userId }),
  });
}

/** POST `/api/auth/impersonation/stop` — return to the operator's own session. */
export async function stopImpersonation(): Promise<Response> {
  return fetch('/api/auth/impersonation/stop', { method: 'POST', credentials: 'include' });
}

interface AuthContextValue {
  user: CurrentUser | null;
  loading: boolean;
  /** Re-fetch `/api/me` — call after start/stop so nav + banner match the new session. */
  refresh: () => Promise<void>;
  /**
   * Set on a definitive 401 — either the initial `/api/me` (no session at boot)
   * or any authenticated request 401ing afterwards (session expired/revoked
   * while the SPA stayed open, via `notifyUnauthenticated`). It is NOT set for
   * other failures (5xx, network error) that also leave `user` null — so
   * callers can redirect to login on a confirmed-missing session without
   * bouncing on a transient identity-fetch error.
   */
  unauthenticated: boolean;
}

const AuthContext = React.createContext<AuthContextValue | null>(null);

type UnauthenticatedListener = () => void;
const unauthenticatedListeners = new Set<UnauthenticatedListener>();

/**
 * Signal that an authenticated request came back with a 401 — the session
 * expired or was revoked while the SPA was already mounted (e.g. the tab was
 * left open overnight). AuthProvider flips to `unauthenticated`, which lets
 * InstallGuard run its existing redirect to the sign-in page rather than
 * leaving pages stuck on load errors. The global query handler
 * (`@/lib/queryClient`) is the sole caller.
 */
export function notifyUnauthenticated(): void {
  for (const listener of unauthenticatedListeners) listener();
}

function subscribeUnauthenticated(listener: UnauthenticatedListener): () => void {
  unauthenticatedListeners.add(listener);
  return () => {
    unauthenticatedListeners.delete(listener);
  };
}

interface AuthProviderProps {
  initialUser?: CurrentUser | null;
  children: React.ReactNode;
}

export function AuthProvider({ initialUser, children }: AuthProviderProps) {
  const [user, setUser] = React.useState<CurrentUser | null>(initialUser ?? null);
  const [loading, setLoading] = React.useState<boolean>(initialUser === undefined);
  const [unauthenticated, setUnauthenticated] = React.useState<boolean>(false);

  // A 401 on any authenticated request after boot (expired/revoked session)
  // flips the same flag the initial /api/me 401 sets, so InstallGuard redirects
  // to sign-in instead of leaving the page on load errors.
  React.useEffect(() => {
    return subscribeUnauthenticated(() => {
      setUser(null);
      setUnauthenticated(true);
      setLoading(false);
    });
  }, []);

  // Resolve identity from /api/me. AuthProvider only resolves identity; it does
  // not redirect. Routing the unauthenticated user is InstallGuard's job, since
  // it knows install status (pre-install → wizard; post-install → /auth/login,
  // docs/10 §dashboard login). We flag a *confirmed* 401 distinctly from other
  // failures so only a genuinely-missing session triggers the login redirect —
  // a transient 5xx/network error leaves `user` null but `unauthenticated` false,
  // so it can't bounce (and loop) on /auth/login.
  const refresh = React.useCallback(async () => {
    try {
      const res = await fetch('/api/me', { credentials: 'include' });
      if (res.status === 401) {
        setUser(null);
        setUnauthenticated(true);
        setLoading(false);
        return;
      }
      if (!res.ok) {
        setUser(null);
        setLoading(false);
        return;
      }
      const body = (await res.json()) as CurrentUser;
      setUser(body);
      setUnauthenticated(false);
      setLoading(false);
    } catch {
      setUser(null);
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    if (initialUser !== undefined) return;
    let cancelled = false;
    void (async () => {
      if (!cancelled) await refresh();
    })();
    return () => {
      cancelled = true;
    };
  }, [initialUser, refresh]);

  const value = React.useMemo(
    () => ({ user, loading, unauthenticated, refresh }),
    [user, loading, unauthenticated, refresh],
  );
  return React.createElement(AuthContext.Provider, { value }, children);
}

export function useAuth(): AuthContextValue {
  const ctx = React.useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}

export function useCurrentUser(): CurrentUser | null {
  return useAuth().user;
}
